import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import api from '../services/api';

// Default content for policies (fallback if API content is empty)
const defaultContent = {
  privacy: `
    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">1. Introduction</h2>
      <p class="mb-4">
        Welcome to Buildex ("we," "our," or "us"). We are committed to protecting your personal information
        and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard
        your information when you use our website and services at buildex.dev.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">2. Information We Collect</h2>
      <p class="mb-4">We collect information that you provide directly to us:</p>
      <ul class="list-disc list-inside space-y-2 ml-4">
        <li><strong>Account Information:</strong> When you register, we collect your username, email address, and password.</li>
        <li><strong>Profile Information:</strong> Optional information you choose to provide such as display name and avatar.</li>
        <li><strong>Usage Data:</strong> Information about how you use our platform, including challenges attempted, submissions, and scores.</li>
        <li><strong>Payment Information:</strong> When you subscribe to premium, payment details are processed securely by our payment provider (Razorpay). We do not store your full card details.</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
      <p class="mb-4">We use the information we collect to:</p>
      <ul class="list-disc list-inside space-y-2 ml-4">
        <li>Provide, maintain, and improve our services</li>
        <li>Process transactions and send related information</li>
        <li>Send you technical notices, updates, and support messages</li>
        <li>Track your progress and provide personalized learning experiences</li>
        <li>Generate anonymized analytics to improve our platform</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">4. Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy, please contact us at support@buildex.dev.
      </p>
    </section>
  `,
  terms: `
    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
      <p class="mb-4">
        By accessing and using Buildex (buildex.dev), you accept and agree to be bound by these Terms and Conditions.
        If you do not agree to these terms, please do not use our services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">2. Description of Service</h2>
      <p class="mb-4">
        Buildex is an online platform that provides system design learning and practice through interactive challenges,
        AI-powered evaluations, and gamified learning experiences. We offer both free and premium subscription tiers.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">3. User Accounts</h2>
      <ul class="list-disc list-inside space-y-2 ml-4">
        <li>You must provide accurate and complete information when creating an account</li>
        <li>You are responsible for maintaining the security of your account credentials</li>
        <li>You must be at least 13 years old to use our services</li>
        <li>One person may not maintain more than one account</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">4. Contact</h2>
      <p>
        For questions about these Terms, contact us at support@buildex.dev.
      </p>
    </section>
  `,
  refunds: `
    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">No Refund Policy</h2>
      <p class="mb-4">
        All sales on Buildex are final. We do not offer refunds for any purchases, including premium subscriptions.
      </p>
      <p class="mb-4">
        Before purchasing a premium subscription, we encourage you to take advantage of our <strong>1-hour free trial</strong>
        to explore all premium features and ensure the platform meets your needs.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Subscription Cancellation</h2>
      <p class="mb-4">
        You may cancel your premium subscription at any time. Upon cancellation:
      </p>
      <ul class="list-disc list-inside space-y-2 ml-4">
        <li>Your premium access will continue until the end of your current billing period</li>
        <li>Your account will not be charged for the next billing cycle</li>
        <li>Your account will automatically revert to the free tier after the subscription period ends</li>
        <li>Your progress, submissions, and data will be retained</li>
        <li><strong>No refunds will be issued for the remaining subscription period</strong></li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Contact Us</h2>
      <p>
        If you have any questions about this policy, please contact us at support@buildex.dev.
      </p>
    </section>
  `,
  shipping: `
    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Digital Product - No Physical Shipping</h2>
      <p class="mb-4">
        Buildex is a digital platform providing online educational services. We do not sell or ship any physical products.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Service Delivery</h2>
      <p class="mb-4">
        Upon successful payment for a premium subscription:
      </p>
      <ul class="list-disc list-inside space-y-2 ml-4">
        <li><strong>Instant Access:</strong> Premium features are activated immediately upon successful payment</li>
        <li><strong>Email Confirmation:</strong> You will receive a confirmation email with your subscription details within minutes</li>
        <li><strong>Account Update:</strong> Your account will be upgraded to premium status automatically</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Contact Us</h2>
      <p>
        For any delivery-related questions, please contact us at support@buildex.dev.
      </p>
    </section>
  `,
  contact: `
    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Get in Touch</h2>
      <p class="mb-6">
        We'd love to hear from you! Whether you have a question about our platform, need technical support,
        or want to provide feedback, our team is here to help.
      </p>
    </section>

    <section class="mb-8">
      <div class="bg-slate-700/50 rounded-lg p-6 border border-slate-600">
        <h3 class="text-lg font-semibold mb-4 text-white">Email Support</h3>
        <p class="mb-2 text-white">For general inquiries and support:</p>
        <a href="mailto:support@buildex.dev" class="text-blue-400 hover:text-blue-300 font-medium">
          support@buildex.dev
        </a>
        <p class="text-sm mt-2 text-white">We typically respond within 24-48 hours</p>
      </div>
    </section>

    <section class="mb-8">
      <h2 class="text-xl font-semibold mb-4">Support Hours</h2>
      <p>
        Our support team is available Monday through Friday, 9:00 AM to 6:00 PM IST.
        We strive to respond to all inquiries within 24-48 business hours.
      </p>
    </section>
  `,
};

const defaultTitles = {
  privacy: 'Privacy Policy',
  terms: 'Terms and Conditions',
  refunds: 'Refund Policy',
  shipping: 'Shipping & Delivery Policy',
  contact: 'Contact Us',
};

const PolicyPages = () => {
  const { page } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await api.get(`/policies/${page}`);
        setPolicy(response.data);
      } catch (error) {
        // Use default content if API fails
        setPolicy({
          title: defaultTitles[page] || 'Policy',
          content: defaultContent[page] || '',
          last_updated: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };

    if (page) {
      fetchPolicy();
    }
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!policy || !defaultTitles[page]) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Page Not Found</h1>
          <Link to="/" className="text-blue-400 hover:text-blue-300">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  // Use API content if available, otherwise use default
  const content = policy.content || defaultContent[page] || '';
  const title = policy.title || defaultTitles[page];
  const lastUpdated = policy.last_updated
    ? new Date(policy.last_updated).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'December 25, 2024';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors w-fit">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Buildex</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12 backdrop-blur-sm">
          <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
          <p className="text-slate-500 mb-8">Last updated: {lastUpdated}</p>

          <div
            className="text-white [&_*]:!text-white [&_a]:!text-blue-400 [&_a:hover]:!text-blue-300 [&_ul]:list-disc [&_ul]:ml-6 [&_li]:mb-2 [&_.bg-gray-50]:!bg-slate-700/50 [&_.bg-gray-50]:!border [&_.bg-gray-50]:!border-slate-600"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </main>
    </div>
  );
};

export default PolicyPages;
